"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { destinoInterno } from "@/lib/caminhoInterno";
import { AuthProvider } from "@/contexts/AuthContext";
import LoginForm from "./components/LoginForm";
import LoginHeader from "./components/LoginHeader";
import LoginFooter from "./components/LoginFooter";
import SapiensLinkForm from "./components/SapiensLinkForm";
import SalesforceLinkForm from "./components/SalesforceLinkForm";

type Step = "login" | "link-sapiens" | "link-salesforce";

function LoginLoadingBlock({ message }: { message: string }) {
  return (
    <div
      className="animate-login-in relative flex w-full max-w-md flex-col items-center justify-center rounded-xl bg-white/95 px-8 py-16 shadow-lg backdrop-blur-sm sm:px-10"
      role="status"
      aria-live="polite"
    >
      <span
        className="h-10 w-10 shrink-0 animate-spin rounded-full border-2 border-amber-primary/25 border-t-amber-primary"
        aria-hidden
      />
      <p className="mt-5 text-center text-sm font-medium text-gray-700">{message}</p>
      <p className="mt-1 text-center text-xs text-gray-500">Aguarde um instante</p>
    </div>
  );
}

function LoginSubmitOverlay({ message }: { message: string }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 px-4 backdrop-blur-[2px]"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="animate-login-in flex w-full max-w-sm flex-col items-center rounded-xl bg-white px-8 py-10 shadow-xl">
        <span
          className="h-10 w-10 shrink-0 animate-spin rounded-full border-2 border-amber-primary/25 border-t-amber-primary"
          aria-hidden
        />
        <p className="mt-5 text-center text-sm font-medium text-gray-800">{message}</p>
        <p className="mt-1 text-center text-xs text-gray-500">Aguarde um instante</p>
      </div>
    </div>
  );
}

function LoginPage() {
  const router = useRouter();
  const { login, isAuthenticated, isLoading, refreshMe } = useAuth();
  const [step, setStep] = useState<Step>("login");
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  // Destino pós-login (`?next=`), capturado no mount antes de qualquer limpeza
  // da query. Só caminho interno (`destinoInterno`: recusa "//", "/\\" e controles). Default: home.
  const nextRef = useRef<string>("/dashboards/home");

  useEffect(() => {
    const url = new URL(window.location.href);
    const destino = destinoInterno(url.searchParams.get("next"));
    if (destino) nextRef.current = destino;
    if (
      url.searchParams.has("identifier") ||
      url.searchParams.has("password") ||
      url.searchParams.has("next")
    ) {
      url.search = "";
      window.history.replaceState({}, "", `${url.pathname}${url.hash}`);
    }
  }, []);

  // Trava a rolagem do documento enquanto a tela de login está montada: o body
  // global usa min-h-screen (100vh = large viewport), maior que a área visível
  // no mobile, o que criava uma barra de rolagem vertical sem conteúdo que a
  // justificasse. O conteúdo do login cabe na viewport e não precisa rolar.
  useEffect(() => {
    const { documentElement, body } = document;
    const prevHtml = documentElement.style.overflow;
    const prevBody = body.style.overflow;
    documentElement.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      documentElement.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated && step === "login") {
      router.replace(nextRef.current);
    }
  }, [isAuthenticated, isLoading, router, step]);

  const handleLogin = async (identifier: string, password: string) => {
    setLoginSubmitting(true);
    try {
      const result = await login(identifier, password);
      if (result.promptSapiensLink) {
        setStep("link-sapiens");
      } else if (result.promptSalesforceLink) {
        setStep("link-salesforce");
      } else {
        router.push(nextRef.current);
      }
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleSkipSapiens = async () => {
    await refreshMe();
    router.push(nextRef.current);
  };

  const handleSapiensLinked = async () => {
    await refreshMe();
    router.push(nextRef.current);
  };

  const handleSkipSalesforce = async () => {
    await refreshMe();
    router.push(nextRef.current);
  };

  const handleSalesforceLinked = async () => {
    await refreshMe();
    router.push(nextRef.current);
  };

  const sessionChecking = step === "login" && isLoading;
  const pendingRedirect = step === "login" && !isLoading && isAuthenticated;
  const showLoginShell = step === "login" && !sessionChecking && !pendingRedirect;

  return (
    <div className="relative flex h-dvh w-full flex-col items-center justify-center overflow-y-auto px-4 py-8">
      {/* Fundo fixo cobre sempre a viewport visível, evitando que o bg do body
          (min-h-screen/100vh) vaze como uma faixa abaixo do gradiente no mobile. */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-gradient-to-br from-amber-sidebar to-amber-primary"
      />
      <LoginHeader />
      {sessionChecking && <LoginLoadingBlock message="Verificando sessão…" />}
      {pendingRedirect && <LoginLoadingBlock message="Abrindo o aplicativo…" />}
      {showLoginShell && (
        <>
          {loginSubmitting && <LoginSubmitOverlay message="Entrando…" />}
          <div className="animate-login-in w-full max-w-md">
            <LoginForm onSubmit={handleLogin} />
          </div>
        </>
      )}
      {step === "link-sapiens" && (
        <SapiensLinkForm onSkip={handleSkipSapiens} onSuccess={handleSapiensLinked} />
      )}
      {step === "link-salesforce" && (
        <SalesforceLinkForm onSkip={handleSkipSalesforce} onSuccess={handleSalesforceLinked} />
      )}
      <LoginFooter />
    </div>
  );
}

export default function LoginPageWrapper() {
  return (
    <AuthProvider>
      <LoginPage />
    </AuthProvider>
  );
}
